import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { X, Volume2, VolumeX, Clock, Sparkles, UserMinus, Palette, Music, Timer, Trophy } from 'lucide-react';
import { getAllThemes } from '../config/wheelThemes';

// Sound style options for spin ticks - using translation keys
const SOUND_STYLES = [
  { id: 'classic', nameKey: 'settings.soundStyles.classic', emoji: '🎵' },
  { id: 'casino', nameKey: 'settings.soundStyles.casino', emoji: '🎰' },
  { id: 'arcade', nameKey: 'settings.soundStyles.arcade', emoji: '🕹️' },
  { id: 'soft', nameKey: 'settings.soundStyles.soft', emoji: '🔔' }
];

// Victory sound options - using translation keys
const VICTORY_SOUNDS = [
  { id: 'fanfare', nameKey: 'settings.victorySounds.fanfare', emoji: '🎺' },
  { id: 'applause', nameKey: 'settings.victorySounds.applause', emoji: '👏' },
  { id: 'cheers', nameKey: 'settings.victorySounds.cheers', emoji: '🎉' },
  { id: 'coins', nameKey: 'settings.victorySounds.coins', emoji: '🪙' },
  { id: 'magic', nameKey: 'settings.victorySounds.magic', emoji: '✨' },
  { id: 'simple', nameKey: 'settings.victorySounds.simple', emoji: '🔔' }
];

const SettingsPanel = ({ settings, onSettingsChange, onClose }) => {
  const { t } = useLanguage();
  const themes = getAllThemes();

  const handleSoundToggle = () => {
    onSettingsChange({ ...settings, sounds: !settings.sounds });
  };

  const handleSoundStyleChange = (styleId) => {
    onSettingsChange({ ...settings, soundStyle: styleId });
  };

  const handleVictorySoundChange = (soundId) => {
    onSettingsChange({ ...settings, victorySound: soundId });
  };

  const handleConfettiToggle = () => {
    onSettingsChange({ ...settings, confetti: !settings.confetti });
  };

  const handleRemoveWinnerToggle = () => {
    onSettingsChange({ ...settings, removeWinnerAfterSpin: !settings.removeWinnerAfterSpin });
  };

  const handleCountdownToggle = () => {
    onSettingsChange({ ...settings, useCountdown: !settings.useCountdown });
  };

  const handleCountdownChange = (e) => {
    onSettingsChange({ ...settings, countdownSeconds: parseInt(e.target.value) });
  };

  const handleDurationChange = (e) => {
    onSettingsChange({ ...settings, spinDuration: parseInt(e.target.value) });
  };

  const handleThemeChange = (themeId) => {
    onSettingsChange({ ...settings, theme: themeId });
  };

  return (
    <div className="panel settings-panel" data-testid="settings-panel">
      <div className="panel-header">
        <h3>{t('wheel.settings')}</h3>
        <button onClick={onClose} className="btn-panel-close" aria-label="Close">
          <X size={20} />
        </button>
      </div>
      
      <div className="panel-content">
        {/* Theme Selector */}
        <div className="setting-item column">
          <div className="setting-label">
            <Palette size={20} />
            <span>{t('wheel.colors') || 'Wheel Theme'}</span>
          </div>
          <div className="theme-grid" data-testid="theme-selector">
            {themes.map((theme) => (
              <button
                key={theme.id}
                className={`theme-option ${settings.theme === theme.id ? 'selected' : ''}`}
                onClick={() => handleThemeChange(theme.id)}
                aria-label={`Select ${theme.name} theme`}
                aria-pressed={settings.theme === theme.id}
                title={theme.name}
              >
                <div className="theme-preview">
                  {theme.colors.slice(0, 4).map((color, idx) => (
                    <div 
                      key={idx} 
                      className="theme-color" 
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <span className="theme-name">{theme.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Sounds Toggle */}
        <div className="setting-item">
          <div className="setting-label">
            {settings.sounds ? <Volume2 size={20} /> : <VolumeX size={20} />}
            <span>{t('wheel.sounds')}</span>
          </div>
          <button
            onClick={handleSoundToggle}
            className={`toggle-btn ${settings.sounds ? 'active' : ''}`}
            aria-pressed={settings.sounds}
            data-testid="sounds-toggle"
          >
            <span className="toggle-slider" />
          </button>
        </div>

        {/* Sound Style Selector - only show if sounds enabled */}
        {settings.sounds && (
          <div className="setting-item column">
            <div className="setting-label">
              <Music size={20} />
              <span>{t('wheel.spinSound') || 'Spin Sound'}</span>
            </div>
            <div className="sound-style-grid" data-testid="sound-style-selector">
              {SOUND_STYLES.map((style) => (
                <button
                  key={style.id}
                  className={`sound-style-option ${(settings.soundStyle || 'classic') === style.id ? 'selected' : ''}`}
                  onClick={() => handleSoundStyleChange(style.id)}
                  aria-pressed={(settings.soundStyle || 'classic') === style.id}
                >
                  <span className="sound-emoji">{style.emoji}</span>
                  <span className="sound-name">{t(style.nameKey) || style.id}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Victory Sound Selector - only show if sounds enabled */}
        {settings.sounds && (
          <div className="setting-item column">
            <div className="setting-label">
              <Trophy size={20} />
              <span>{t('wheel.victorySound') || 'Victory Sound'}</span>
            </div>
            <div className="sound-style-grid victory-sounds" data-testid="victory-sound-selector">
              {VICTORY_SOUNDS.map((sound) => (
                <button
                  key={sound.id}
                  className={`sound-style-option ${(settings.victorySound || 'fanfare') === sound.id ? 'selected' : ''}`}
                  onClick={() => handleVictorySoundChange(sound.id)}
                  aria-pressed={(settings.victorySound || 'fanfare') === sound.id}
                >
                  <span className="sound-emoji">{sound.emoji}</span>
                  <span className="sound-name">{t(sound.nameKey) || sound.id}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Confetti Toggle */}
        <div className="setting-item">
          <div className="setting-label">
            <Sparkles size={20} />
            <span>{t('wheel.confetti') || 'Confetti'}</span>
          </div>
          <button
            onClick={handleConfettiToggle}
            className={`toggle-btn ${settings.confetti ? 'active' : ''}`}
            aria-pressed={settings.confetti}
            data-testid="confetti-toggle"
          >
            <span className="toggle-slider" />
          </button>
        </div>

        {/* Auto Remove Winner Toggle */}
        <div className="setting-item">
          <div className="setting-label">
            <UserMinus size={20} />
            <span>{t('wheel.removeWinner') || 'Auto-remove winner'}</span>
          </div>
          <button
            onClick={handleRemoveWinnerToggle}
            className={`toggle-btn ${settings.removeWinnerAfterSpin ? 'active' : ''}`}
            aria-pressed={settings.removeWinnerAfterSpin}
            data-testid="auto-remove-toggle"
          >
            <span className="toggle-slider" />
          </button>
        </div>

        {/* Countdown Timer Toggle */}
        <div className="setting-item">
          <div className="setting-label">
            <Timer size={20} />
            <span>{t('wheel.countdown') || 'Countdown Timer'}</span>
          </div>
          <button
            onClick={handleCountdownToggle}
            className={`toggle-btn ${settings.useCountdown ? 'active' : ''}`}
            aria-pressed={settings.useCountdown}
            data-testid="countdown-toggle"
          >
            <span className="toggle-slider" />
          </button>
        </div>

        {/* Countdown Duration - only show if countdown enabled */}
        {settings.useCountdown && (
          <div className="setting-item column">
            <div className="setting-label">
              <span>Countdown: {settings.countdownSeconds || 3}s</span>
            </div>
            <input
              type="range"
              min="1"
              max="10"
              step="1"
              value={settings.countdownSeconds || 3}
              onChange={handleCountdownChange}
              className="range-input"
              data-testid="countdown-slider"
            />
            <div className="range-labels">
              <span>1s</span>
              <span>10s</span>
            </div>
          </div>
        )}

        {/* Spin Duration */}
        <div className="setting-item column">
          <div className="setting-label">
            <Clock size={20} />
            <span>{t('wheel.spinDuration') || 'Spin Duration'}</span>
            <span className="setting-value">{settings.spinDuration / 1000}s</span>
          </div>
          <input
            type="range"
            min="2000"
            max="10000"
            step="500"
            value={settings.spinDuration}
            onChange={handleDurationChange}
            className="range-input"
            data-testid="duration-slider"
          />
          <div className="range-labels">
            <span>2s</span>
            <span>10s</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
